import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { returnCasesHttp } from '@/services/returnCases'
import { toast } from '@/store/toastStore'
import type {
  ExtractionCandidateReviewItem,
  FactModule,
  OfficialImportResponse,
  OfficialSourceFamily,
  ReconciliationComparisonKind,
  ReconciliationItem,
} from '@/types/returnCase.types'

interface Props {
  caseId: string
  enabled: boolean
  canResolve: boolean
  canImport: boolean
  modules: FactModule[]
  onChanged?: () => Promise<void> | void
}

const comparisonKinds: Array<{
  value: ReconciliationComparisonKind
  label: string
  records: string[]
}> = [
  { value: 'SALARY_TDS', label: 'Salary and TDS', records: ['SALARY', 'TDS'] },
  { value: 'INTEREST_AIS', label: 'Interest and AIS/TIS', records: ['INTEREST'] },
  { value: 'CHALLAN_CREDIT', label: 'Challan credits', records: ['CHALLAN'] },
  { value: 'LOAN_INTEREST', label: 'Housing-loan interest', records: ['LOAN_INTEREST'] },
  { value: 'BROKER_AIS', label: 'Broker transfers and AIS', records: ['CAPITAL_TRANSFER'] },
]

const sourceFamilies: Array<{ value: OfficialSourceFamily; label: string; modules: string[] }> = [
  { value: 'FORM_16', label: 'Form 16', modules: ['SALARY', 'TAX_CREDITS'] },
  { value: 'AIS', label: 'AIS', modules: ['OTHER_SOURCES', 'TAX_CREDITS', 'CAPITAL_GAINS'] },
  { value: 'TIS', label: 'TIS', modules: ['OTHER_SOURCES', 'TAX_CREDITS', 'CAPITAL_GAINS'] },
  { value: 'FORM_26AS', label: 'Form 26AS', modules: ['TAX_CREDITS'] },
  {
    value: 'BANK_INTEREST_CERTIFICATE',
    label: 'Bank interest certificate',
    modules: ['OTHER_SOURCES'],
  },
  { value: 'HOUSING_LOAN_CERTIFICATE', label: 'Housing-loan certificate', modules: ['PROPERTY'] },
  { value: 'BUSINESS_STATEMENT', label: 'Sanitized business statement', modules: ['BUSINESS'] },
]

const candidateKind = (candidate: ExtractionCandidateReviewItem) => {
  const value = candidate.normalized_value
  return value && typeof value === 'object' && 'record_kind' in value
    ? String((value as Record<string, unknown>).record_kind)
    : ''
}

const pretty = (value: unknown) => JSON.stringify(value, null, 2)

export default function ReconciliationPanel({
  caseId,
  enabled,
  canResolve,
  canImport,
  modules,
  onChanged,
}: Props) {
  const queryClient = useQueryClient()
  const reconciliations = useQuery({
    queryKey: ['reconciliations', caseId],
    queryFn: () => returnCasesHttp.reconciliations(caseId),
    enabled,
  })
  const candidates = useQuery({
    queryKey: ['extraction-candidates', caseId],
    queryFn: () => returnCasesHttp.extractionCandidates(caseId),
    enabled,
  })
  const evidence = useQuery({
    queryKey: ['return-case-evidence', caseId],
    queryFn: () => returnCasesHttp.evidenceRail(caseId),
    enabled: enabled && canImport,
  })
  const currentRuns = reconciliations.data?.runs.filter(run => run.status === 'CURRENT') ?? []
  const [selectedItemId, setSelectedItemId] = useState('')
  const items = currentRuns.flatMap(run => run.items)
  const selected = items.find(item => item.id === selectedItemId) ?? items[0]
  const [annotation, setAnnotation] = useState('')
  const [action, setAction] = useState<
    | 'ACCEPT_LOCAL'
    | 'REQUEST_SOURCE_CORRECTION'
    | 'ASK_CUSTOMER'
    | 'MARK_DUPLICATE'
    | 'CORRECT_LOCAL_SEPARATELY'
    | 'WAIVE_WITH_CA_REASON'
  >('ASK_CUSTOMER')
  const [reasonCode, setReasonCode] = useState('REVIEW_COMPLETED')
  const [narrative, setNarrative] = useState('')
  const [sourceFamily, setSourceFamily] = useState<OfficialSourceFamily>('AIS')
  const [bindingId, setBindingId] = useState('')
  const [moduleId, setModuleId] = useState('')
  const [importResult, setImportResult] = useState<OfficialImportResponse | null>(null)

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['reconciliations', caseId] })
    await onChanged?.()
  }
  const start = useMutation({
    mutationFn: async (kind: ReconciliationComparisonKind) => {
      if (!reconciliations.data || !candidates.data) throw new Error('Source data has not loaded')
      const definition = comparisonKinds.find(item => item.value === kind)!
      const ids = candidates.data.items
        .filter(candidate => definition.records.includes(candidateKind(candidate)))
        .map(candidate => candidate.candidate_id)
      if (!ids.length) throw new Error(`No parsed ${definition.label} candidates are available`)
      return returnCasesHttp.startReconciliation(caseId, {
        expected_revision: reconciliations.data.case_revision,
        comparison_kind: kind,
        source_candidate_ids: ids,
        reason: 'Prepare source-to-return reconciliation',
      })
    },
    onSuccess: async () => {
      await refresh()
      toast.success('Reconciliation prepared')
    },
    onError: error =>
      toast.error(
        'Reconciliation could not run',
        error instanceof Error ? error.message : 'Review source candidates.'
      ),
  })
  const officialImport = useMutation({
    mutationFn: async () => {
      if (!reconciliations.data || !bindingId || !moduleId) {
        throw new Error('Choose active evidence and a compatible typed module')
      }
      const family = sourceFamilies.find(item => item.value === sourceFamily)!
      const module = modules.find(item => item.id === moduleId && item.status === 'ACTIVE')
      if (!module || !family.modules.includes(module.module_type)) {
        throw new Error('The selected module is not compatible with this source')
      }
      return returnCasesHttp.startOfficialImport(caseId, {
        expected_revision: reconciliations.data.case_revision,
        binding_id: bindingId,
        module_instance_id: moduleId,
        source_family: sourceFamily,
        reason: 'Parse reviewed immutable evidence for reconciliation',
      })
    },
    onSuccess: async result => {
      setImportResult(result)
      await refresh()
      toast.success(
        result.projected
          ? 'Official source candidates prepared'
          : `Import requires review: ${result.terminal_failure_code ?? result.state}`
      )
    },
    onError: error =>
      toast.error(
        'Official import rejected',
        error instanceof Error ? error.message : 'Review the evidence and module.'
      ),
  })
  const annotate = useMutation({
    mutationFn: async (item: ReconciliationItem) => {
      if (!reconciliations.data || annotation.trim().length < 3)
        throw new Error('Enter an annotation')
      return returnCasesHttp.annotateReconciliation(caseId, item.id, {
        expected_revision: reconciliations.data.case_revision,
        displayed_item_sha256: item.item_sha256,
        annotation: annotation.trim(),
      })
    },
    onSuccess: async () => {
      setAnnotation('')
      await refresh()
      toast.success('Annotation saved')
    },
    onError: error =>
      toast.error(
        'Annotation rejected',
        error instanceof Error ? error.message : 'Reload the item.'
      ),
  })
  const decide = useMutation({
    mutationFn: async (item: ReconciliationItem) => {
      if (!reconciliations.data || narrative.trim().length < 5)
        throw new Error('Explain the decision')
      return returnCasesHttp.decideReconciliation(caseId, item.id, {
        expected_revision: reconciliations.data.case_revision,
        displayed_item_sha256: item.item_sha256,
        action,
        reason_code: reasonCode
          .trim()
          .toUpperCase()
          .replace(/[^A-Z0-9_]/g, '_'),
        narrative: narrative.trim(),
      })
    },
    onSuccess: async () => {
      setNarrative('')
      await refresh()
      toast.success('Reconciliation decision recorded')
    },
    onError: error =>
      toast.error(
        'Decision rejected',
        error instanceof Error ? error.message : 'Reload the current item.'
      ),
  })

  const staleRuns = useMemo(
    () => reconciliations.data?.runs.filter(run => run.status === 'STALE') ?? [],
    [reconciliations.data]
  )
  const compatibleModules = useMemo(() => {
    const allowed = sourceFamilies.find(item => item.value === sourceFamily)?.modules ?? []
    return modules.filter(item => item.status === 'ACTIVE' && allowed.includes(item.module_type))
  }, [modules, sourceFamily])
  const activeBindings =
    evidence.data?.bindings.filter(
      item => item.status === 'ACTIVE' && item.document_state === 'AVAILABLE'
    ) ?? []
  if (!enabled) return null
  return (
    <section className="card mt-6 p-5" aria-labelledby="reconciliation-heading">
      <h2 id="reconciliation-heading" className="font-semibold">
        Reconciliation centre
      </h2>
      <p className="mt-1 text-sm text-text-secondary">
        Compare typed return records with parsed sources. Both sides and every prior decision remain
        visible.
      </p>
      {canImport && (
        <fieldset className="mt-4 grid gap-3 rounded border border-border p-3 md:grid-cols-3">
          <legend className="px-1 text-sm font-medium">Prepare official source candidates</legend>
          <label className="text-sm" htmlFor="official-source-family">
            Source family
            <select
              id="official-source-family"
              className="input mt-1 w-full"
              value={sourceFamily}
              onChange={event => {
                setSourceFamily(event.target.value as OfficialSourceFamily)
                setModuleId('')
              }}
            >
              {sourceFamilies.map(item => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm" htmlFor="official-source-binding">
            Reviewed evidence
            <select
              id="official-source-binding"
              className="input mt-1 w-full"
              value={bindingId}
              onChange={event => setBindingId(event.target.value)}
            >
              <option value="">Choose evidence</option>
              {activeBindings.map(item => (
                <option key={item.id} value={item.id}>
                  Version {item.document_version_number} · {item.document_id.slice(0, 8)}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm" htmlFor="official-source-module">
            Typed module
            <select
              id="official-source-module"
              className="input mt-1 w-full"
              value={moduleId}
              onChange={event => setModuleId(event.target.value)}
            >
              <option value="">Choose module</option>
              {compatibleModules.map(item => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <button
            className="btn btn-secondary md:col-span-3"
            type="button"
            onClick={() => officialImport.mutate()}
            disabled={!bindingId || !moduleId || officialImport.isPending}
          >
            Parse into review candidates
          </button>
          {importResult && (
            <p className="md:col-span-3 text-sm" role="status">
              Import {importResult.state} · {importResult.candidate_count} candidate
              {importResult.candidate_count === 1 ? '' : 's'} ·{' '}
              {importResult.projected ? 'ready for review' : 'not projected'}
            </p>
          )}
          {evidence.isError && (
            <p className="alert alert-error md:col-span-3" role="alert">
              Available evidence could not be loaded.
            </p>
          )}
        </fieldset>
      )}
      <div className="mt-4 flex flex-wrap gap-2" aria-label="Start reconciliation">
        {comparisonKinds.map(kind => (
          <button
            className="btn btn-secondary"
            type="button"
            key={kind.value}
            onClick={() => start.mutate(kind.value)}
            disabled={start.isPending}
          >
            Run {kind.label}
          </button>
        ))}
      </div>
      {reconciliations.isLoading && <p className="mt-4">Loading reconciliation history…</p>}
      {reconciliations.isError && (
        <div className="alert alert-error mt-4" role="alert">
          Reconciliation history could not be loaded.
        </div>
      )}
      {staleRuns.length > 0 && (
        <details className="mt-4">
          <summary>
            {staleRuns.length} stale run{staleRuns.length === 1 ? '' : 's'} retained in history
          </summary>
          <ul className="mt-2 list-disc pl-5 text-sm">
            {staleRuns.map(run => (
              <li key={run.id}>
                {run.comparison_kind} run {run.run_number}: {run.stale_reason}
              </li>
            ))}
          </ul>
        </details>
      )}
      {items.length === 0 && !reconciliations.isLoading && (
        <p className="mt-4 text-sm text-text-secondary">No current reconciliation items.</p>
      )}
      {selected && reconciliations.data && (
        <div className="mt-4">
          <label htmlFor="reconciliation-item" className="text-sm">
            Reconciliation item
          </label>
          <select
            id="reconciliation-item"
            className="input mt-1 w-full"
            value={selected.id}
            onChange={event => setSelectedItemId(event.target.value)}
          >
            {items.map(item => (
              <option key={item.id} value={item.id}>
                {item.record_kind} · {item.identity_key || 'unmatched'} · {item.state}
              </option>
            ))}
          </select>
          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <section
              className="rounded border border-border p-3"
              aria-labelledby="return-side-heading"
            >
              <h3 id="return-side-heading" className="font-medium">
                Return side
              </h3>
              <p className="mt-2 text-lg">INR {selected.local_amount ?? '—'}</p>
              <pre className="mt-2 overflow-auto whitespace-pre-wrap text-xs">
                {pretty(selected.local_snapshot)}
              </pre>
            </section>
            <section
              className="rounded border border-border p-3"
              aria-labelledby="comparison-state-heading"
            >
              <h3 id="comparison-state-heading" className="font-medium">
                Comparison
              </h3>
              <p className={`mt-2 badge ${selected.catastrophic_magnitude ? 'badge-error' : ''}`}>
                {selected.state}
              </p>
              <p className="mt-2 text-sm">{selected.explanation_code}</p>
              <p className="mt-2 text-sm">Difference: INR {selected.difference_amount ?? '—'}</p>
              <p className="mt-2 break-all text-xs">
                Policy {String(selected.tolerance_snapshot.policy_version)}
              </p>
              {selected.catastrophic_magnitude && (
                <div className="alert alert-error mt-3" role="alert">
                  Catastrophic magnitude mismatch. Candidate acceptance remains blocked.
                </div>
              )}
            </section>
            <section
              className="rounded border border-border p-3"
              aria-labelledby="source-side-heading"
            >
              <h3 id="source-side-heading" className="font-medium">
                Source side
              </h3>
              <p className="mt-2 text-lg">INR {selected.source_amount ?? '—'}</p>
              <pre className="mt-2 overflow-auto whitespace-pre-wrap text-xs">
                {pretty(selected.source_snapshot)}
              </pre>
            </section>
          </div>
          {selected.decision && (
            <div className="mt-3 rounded border border-border p-3 text-sm" role="status">
              <strong>{selected.decision.action}</strong> · {selected.decision.reason_code}
              <p>{selected.decision.narrative}</p>
            </div>
          )}
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            <div>
              <label htmlFor="reconciliation-annotation" className="text-sm">
                Preparation annotation
              </label>
              <textarea
                id="reconciliation-annotation"
                className="input mt-1 w-full"
                value={annotation}
                onChange={event => setAnnotation(event.target.value)}
              />
              <button
                className="btn btn-secondary mt-2"
                type="button"
                onClick={() => annotate.mutate(selected)}
                disabled={annotate.isPending}
              >
                Save annotation
              </button>
            </div>
            {canResolve && selected.state !== 'RESOLVED' && (
              <div>
                <label htmlFor="reconciliation-action" className="text-sm">
                  CA decision
                </label>
                <select
                  id="reconciliation-action"
                  className="input mt-1 w-full"
                  value={action}
                  onChange={event => setAction(event.target.value as typeof action)}
                >
                  <option value="ACCEPT_LOCAL">Accept local record</option>
                  <option value="REQUEST_SOURCE_CORRECTION">Request source correction</option>
                  <option value="ASK_CUSTOMER">Ask customer</option>
                  <option value="MARK_DUPLICATE">Mark duplicate</option>
                  <option value="CORRECT_LOCAL_SEPARATELY">Correct local record separately</option>
                  {!selected.catastrophic_magnitude &&
                    selected.original_state !== 'IDENTITY_MISMATCH' && (
                      <option value="WAIVE_WITH_CA_REASON">Waive with CA reason</option>
                    )}
                </select>
                <label htmlFor="reconciliation-reason-code" className="mt-2 block text-sm">
                  Reason code
                </label>
                <input
                  id="reconciliation-reason-code"
                  className="input mt-1 w-full"
                  value={reasonCode}
                  onChange={event => setReasonCode(event.target.value)}
                />
                <label htmlFor="reconciliation-narrative" className="mt-2 block text-sm">
                  Decision explanation
                </label>
                <textarea
                  id="reconciliation-narrative"
                  className="input mt-1 w-full"
                  value={narrative}
                  onChange={event => setNarrative(event.target.value)}
                />
                <button
                  className="btn btn-primary mt-2"
                  type="button"
                  onClick={() => decide.mutate(selected)}
                  disabled={decide.isPending}
                >
                  Record this decision
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
